import { API_ENDPOINTS } from '../config/api'
import { getActiveOrgId } from '../utils/orgUtils'

function withOrganizationScope(url: string): string {
  const orgId = getActiveOrgId()
  if (!orgId) return url
  const sep = url.includes('?') ? '&' : '?'
  return `${url}${sep}organization_id=${encodeURIComponent(orgId)}`
}

function getAuthHeaders(): HeadersInit {
  const token =
    typeof window !== 'undefined'
      ? localStorage.getItem('token') || localStorage.getItem('accessToken')
      : null
  return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' }
}

async function parseJson(res: Response) {
  const body = await res.json()
  if (!res.ok) throw new Error(body?.message || body?.error || 'Request failed')
  return body?.data ?? body
}

export type WorkflowProgressStep = {
  key: string
  labelKey: string
  status: 'pending' | 'current' | 'complete'
  blockerCount?: number
}

export type ComplianceWorkflowPayload = {
  workflowEnabled?: boolean
  standardCode: string
  workflowStage: string
  isActive?: boolean
  scopeDefinition?: Record<string, unknown>
  progressRail?: WorkflowProgressStep[]
  detectedApplicability?: Array<{ type: string; label: string; message: string }>
  informationSystems?: Array<{
    id: string
    name: string
    code?: string
    environment?: string
    criticality?: string
    inStandardScope?: boolean
    dataCategories?: string[]
  }>
  explanations?: Array<{ type: string; message: string }>
  crossStandardReuseEnabled?: boolean
  operationalEvents?: {
    enabled?: boolean
    timeline?: Array<{ id: string; at: string; eventType: string; severity: string; summary: string }>
    regressions?: unknown[]
    recoveries?: unknown[]
    certificationRisk?: unknown[]
  } | null
  operationalAssurance?: {
    enabled?: boolean
    degraded?: unknown[]
    recovering?: unknown[]
    states?: unknown[]
  } | null
  reuseQueue?: Array<{
    id: string
    sourceStandardCode: string
    targetStandardCode: string
    reuseConfidence: number
    scopeCompatibility: string
    status: string
    reuseReason?: string
    explanationPayload?: unknown
    sourceEvidence?: { id: string; title: string }
    canonicalControl?: { id: string; code: string }
    informationSystem?: { id: string; name: string }
  }>
}

export async function fetchWorkflowList() {
  const res = await fetch(withOrganizationScope(API_ENDPOINTS.COMPLIANCE_WORKFLOW_LIST), {
    headers: getAuthHeaders(),
  })
  return parseJson(res) as { workflows: Array<{ standardCode: string; workflowStage: string }> }
}

export async function fetchWorkflow(standardCode: string) {
  const res = await fetch(withOrganizationScope(API_ENDPOINTS.COMPLIANCE_WORKFLOW(standardCode)), {
    headers: getAuthHeaders(),
  })
  return parseJson(res) as ComplianceWorkflowPayload
}

export async function activateWorkflow(standardCode: string, reason?: string) {
  const res = await fetch(withOrganizationScope(API_ENDPOINTS.COMPLIANCE_WORKFLOW_ACTIVATE(standardCode)), {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ reason }),
  })
  return parseJson(res)
}

export async function saveWorkflowScope(standardCode: string, scope: Record<string, unknown>) {
  const res = await fetch(withOrganizationScope(API_ENDPOINTS.COMPLIANCE_WORKFLOW_SCOPE(standardCode)), {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(scope),
  })
  return parseJson(res)
}

export async function finalizeWorkflowScope(standardCode: string) {
  const res = await fetch(withOrganizationScope(API_ENDPOINTS.COMPLIANCE_WORKFLOW_FINALIZE_SCOPE(standardCode)), {
    method: 'POST',
    headers: getAuthHeaders(),
  })
  return parseJson(res)
}

export async function fetchWorkflowApplicability(standardCode: string) {
  const res = await fetch(withOrganizationScope(API_ENDPOINTS.COMPLIANCE_WORKFLOW_APPLICABILITY(standardCode)), {
    headers: getAuthHeaders(),
  })
  return parseJson(res)
}

export async function fetchWorkflowReadiness(standardCode: string) {
  const res = await fetch(withOrganizationScope(API_ENDPOINTS.COMPLIANCE_WORKFLOW_READINESS(standardCode)), {
    headers: getAuthHeaders(),
  })
  return parseJson(res)
}

export async function fetchWorkflowBlockers(standardCode: string) {
  const res = await fetch(withOrganizationScope(API_ENDPOINTS.COMPLIANCE_WORKFLOW_BLOCKERS(standardCode)), {
    headers: getAuthHeaders(),
  })
  return parseJson(res)
}

export async function fetchWorkflowTopology(standardCode: string) {
  const res = await fetch(withOrganizationScope(API_ENDPOINTS.COMPLIANCE_WORKFLOW_TOPOLOGY(standardCode)), {
    headers: getAuthHeaders(),
  })
  return parseJson(res)
}
